import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, errorMessage } from '@/lib/api'
import { SELF_PICKUP } from '@/lib/pickupHours'
import { useShippingInfo } from '@/lib/addresses'
import type { OptionSets, Order } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import Modal from '@/components/ui/Modal'
import { useToast } from '@/components/ui/toast'
import TrackingLinkBox from '@/components/order/TrackingLinkBox'


export interface ShipmentDraft {
  id: number
  number: string
  carrier: string
  tracking_number: string
  share_token?: string | null
  /** The standing courier API booking, if any - these fields only edit the mirror. */
  booked?: { carrier: string; tracking_number: string } | null
}

export function toShipment(order: Order): ShipmentDraft {
  return {
    id: order.id,
    number: order.number,
    carrier: order.carrier ?? order.delivery_method ?? '',
    tracking_number: order.tracking_number ?? '',
    share_token: order.job?.share_token,
    booked: order.shipment ? { carrier: order.shipment.carrier, tracking_number: order.shipment.tracking_number } : null,
  }
}

/**
 * Records the courier hand-off for an order. Carriers come from the admin's
 * rate cards (Settings > Delivery); the seeded option set only fills in while
 * shipping info is still loading - same fallback as checkout and CreateOrder.
 */
export default function ShipmentModal({
  shipment,
  onChange,
  onClose,
}: {
  shipment: ShipmentDraft | null
  onChange: (draft: ShipmentDraft) => void
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const shippingInfo = useShippingInfo()

  const optionSets = useQuery({
    queryKey: ['options'],
    queryFn: async () => (await api.get<{ data: OptionSets }>('/options')).data.data,
  })

  const saveShipment = useMutation({
    mutationFn: async (d: ShipmentDraft) =>
      (await api.patch(`/admin/orders/${d.id}/shipment`, {
        carrier: d.carrier || null,
        tracking_number: d.tracking_number || null,
      })).data,
    onSuccess: (res) => {
      // The shared pages key by ['orders-page', basePath, ...] and
      // ['order-detail', basePath, id]; the prefix alone matches both consoles.
      queryClient.invalidateQueries({ queryKey: ['orders-page'] })
      queryClient.invalidateQueries({ queryKey: ['order-detail'] })
      onClose()
      toast.success(res.message ?? 'Shipment details saved.')
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const courierOptions =
    shippingInfo.data?.couriers ??
    (optionSets.data?.delivery_methods ?? []).filter((method) => method !== SELF_PICKUP)
  const carrierValues = [SELF_PICKUP, ...courierOptions]
  // An older order can carry a carrier that no rate card names any more -
  // keep it selectable so opening and saving does not silently blank it.
  if (shipment?.carrier && !carrierValues.some((value) => value.toLowerCase() === shipment.carrier.toLowerCase())) {
    carrierValues.unshift(shipment.carrier)
  }

  return (
    <Modal
      open={Boolean(shipment)}
      onClose={onClose}
      title={shipment ? `Shipment - ${shipment.number}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saveShipment.isPending} onClick={() => shipment && saveShipment.mutate(shipment)}>
            Save
          </Button>
        </>
      }
    >
      {shipment && (
        <div className="space-y-3">
          <Field label="Carrier">
            <Select
              value={shipment.carrier}
              onChange={(v) => onChange({ ...shipment, carrier: v })}
              placeholder="Not shipped yet..."
              options={carrierValues.map((value) => ({ value, label: value }))}
            />
          </Field>
          <Field label="Tracking number">
            <Input
              value={shipment.tracking_number}
              onChange={(e) => onChange({ ...shipment, tracking_number: e.target.value })}
              placeholder="Consignment / AWB number from the courier"
            />
          </Field>
          {shipment.booked ? (
            <p className="text-xs text-muted">
              Booked with {shipment.booked.carrier} ({shipment.booked.tracking_number}). Void that booking on the order page before editing these by hand.
            </p>
          ) : (
            <p className="text-xs text-muted">
              Saving shows a Track parcel link on the customer's order page and their shared tracking page.
            </p>
          )}
          {shipment.share_token && <TrackingLinkBox shareToken={shipment.share_token} />}
        </div>
      )}
    </Modal>
  )
}
